import React from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { Flex, Box, Text, Icon, Button, getPaletteColor, Tooltip as DesignToolTip } from 'pcln-design-system'
import styled from 'styled-components'
import { AirlineImage } from '@pcln/fly-ui-components'
import MultipleAirlines from '../MultipleAirlinesIcon'
import AirlineTitle from '../AirlineTitle/'
import Tooltip from '../Tooltip'
import Stops from '../Stops'
import SliceCheckbox from '../SliceCheckbox'
import { stopsLabel } from '../../constants'
import buildReadableItinDescription from '../../util/buildScreenReaderText/buildScreenReaderText'
import { getAirlinesForThisSlice } from './helpers'
import DayDateBox from './DayDateBox'

/** = Styles */
const StyledSlice = styled(Flex)`
  border-bottom: 1px solid ${getPaletteColor('border.base')};
  border-bottom-width: ${(props) => (props.showFareBrandShelf ? '0px' : '1px')};
  padding: 12px 8px;
  background-color: ${(props) =>
    props.isSelected || props.isHovered ? getPaletteColor('background.light')(props) : ''};

  &:last-of-type {
    border-bottom: ${(props) =>
      props.isSelected ? `2px solid ${getPaletteColor('border.base')(props)}` : 'transparent'};
  }

  ${(props) => props.theme.flyQueries.tablet} {
    padding: 16px 16px 16px ${(props) => (props.showFareBrandShelf ? '0px' : '8px')};
    justify-content: baseline;
  }

  ${(props) => props.theme.flyQueries.mobile} {
    flex-wrap: wrap;
  }
`

const MobileSliceSelectionWrapper = styled(Box)`
  ${(props) => props.theme.flyQueries.tablet} {
    display: none;
  }
`

const StyledToolTip = styled(DesignToolTip)`
  white-space: nowrap;
`

const OvernightBox = styled(Box)`
  display: contents;
`

const StyledSliceSelection = styled(Box)`
  ${(props) => !props.mobileSliceFilter && 'display: none;'};
  font-weight: 300;
  align-self: center;
  margin-right: 12px;
  .mobile-checkbox__fake-box {
    height: 20px;
    width: 20px;
    border-radius: ${(props) => props.theme.borderRadii.md};
    border: 2px solid ${getPaletteColor('border.base')};

    &:hover {
      border: 2px solid ${getPaletteColor('primary.base')};
    }
  }

  .mobile-checkbox .icon-utility-checkLight {
    margin: 1px 0 0 4px;
  }

  ${(props) => props.theme.flyQueries.tablet} {
    display: block;
  }

  ${(props) => props.theme.flyQueries.desktophd} {
    margin-right: 20px;
  }
`

const ImageAndTitleText = styled(Flex)`
  position: relative;
  width: 24px;
  margin-right: 8px;
  margin-left: 8px;
  text-align: center;

  ${(props) => props.theme.flyQueries.tablet} {
    width: 64px;
  }

  ${(props) => props.theme.flyQueries.desktophd} {
    width: 64px;
  }

  ${(props) => props.theme.flyQueries.mobilelg} {
    @media (max-width: 1024px) {
      margin-top: 4px;
    }
  }

  ${(props) => props.theme.flyQueries.mobile} {
    margin-right: 8px;
    margin-top: 4px;
  }
`

const FormattedItineraryDetailFlex = styled(Flex)`
  margin-left: ${(props) => (props.mobileSliceFilter ? '0px' : '8px')};
  flex: 1 1 auto;
`

const TimeAndAirportItem = styled(Box)`
  position: relative;
  line-height: 1.25;
  font-size: 14px;
  width: ${(props) => (props.isCheckoutSlice ? 'auto' : '3.25em')};
  color: ${getPaletteColor('text.base')};

  &.departure {
    text-align: right;
  }

  &.arrival {
    text-align: left;
  }

  abbr {
    font-weight: 500;
    font-size: 11px;
    margin-top: 8px;
    text-decoration: none;

    &.pclnOne-highlighted {
      background-color: ${getPaletteColor('alert.light')};
    }
  }

  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 16px;

    abbr {
      font-size: 13px;
    }
  }
`

const StyledToolTipDivLeft = styled(Flex)`
  position: relative;
  bottom: 19px;
  margin-left: 15px;
  z-index: 1;
`

const StyledToolTipDivRight = styled(Flex)`
  position: relative;
  bottom: 19px;
  margin-right: 20px;
  z-index: 1;
`

const PlusDaySuper = styled.sup`
  vertical-align: super;
  font-size: 9px;
  font-weight: 600;
`

const DurationTimeBox = styled(Box)`
  color: ${getPaletteColor('text.light')};
  font-size: 11px;
  font-weight: 400;
  text-align: right;
  vertical-align: text-bottom;
  line-height: 14px;
  flex-grow: 1;
  display: none;

  ${(props) => props.theme.flyQueries.tablet} {
    display: inline-block;
  }

  ${(props) => props.theme.flyQueries.desktop} {
    font-size: 13px;
  }

  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 14px;
    line-height: 16.25px;
  }
`

const StyledMultipleAirlinesWrapper = styled(Flex)`
  height: 18px;
  width: 24px;
  ${(props) => props.theme.flyQueries.desktop} {
    width: 36px;
    height: 27px;
  }
`

const StyledIcon = styled(Icon)`
  line-height: 1;
  vertical-align: inherit;
`

const StyledStopsText = styled(Text.span)`
  text-transform: capitalize;
  font-weight: 500;
  line-height: 16px;
  color: ${getPaletteColor('text.base')};
  padding: 0;

  ${(props) => props.theme.flyQueries.desktop} {
    line-height: 20px;
  }
`

const StyledFareBrandButton = styled(Button)`
  cursor: pointer;
  line-height: 16px;
  display: inline-block;
  color: ${getPaletteColor('primary.base')};
  padding: 0;
  background-color: transparent;
  font-weight: ${(props) => props.theme.regular};

  ${(props) => props.theme.flyQueries.desktop} {
    line-height: 20px;
  }

  :hover {
    background-color: transparent;
  }
`

const StyledAirlineImage = styled(AirlineImage)`
  margin: auto;
`

export function getAirportCode(currentSliceIndex, openJawStatus, GDSCitySuggestionStatus) {
  const HIGHLIGHT_CLASS_NAME = 'pclnOne-highlighted'
  let highlightedOrigin = ''
  let highlightedDestination = ''

  if (openJawStatus) {
    if (currentSliceIndex === 1) {
      highlightedOrigin = openJawStatus.returning.origin ? HIGHLIGHT_CLASS_NAME : ''
      highlightedDestination = openJawStatus.returning.destination ? HIGHLIGHT_CLASS_NAME : ''
    } else {
      highlightedOrigin = openJawStatus.departing.origin ? HIGHLIGHT_CLASS_NAME : ''
      highlightedDestination = openJawStatus.departing.destination ? HIGHLIGHT_CLASS_NAME : ''
    }
  }

  if (GDSCitySuggestionStatus) {
    if (!highlightedOrigin && GDSCitySuggestionStatus.differentOrigin) {
      highlightedOrigin = HIGHLIGHT_CLASS_NAME
    }
    if (!highlightedDestination && GDSCitySuggestionStatus.differentDestination) {
      highlightedDestination = HIGHLIGHT_CLASS_NAME
    }
  }
  return {
    origin: highlightedOrigin,
    destination: highlightedDestination,
  }
}

export function getAirlineName(airlines) {
  if (airlines.length === 1) {
    return airlines[0].name
  }
  return ''
}

export function getOvernight(slice, getOnlyData = false) {
  const departingTime = slice.departInfo?.time?.dateTime
  const arrivalTime = slice.arrivalInfo?.time?.dateTime
  const departDateTime = moment(departingTime)
  const arrivalDateTime = moment(arrivalTime)
  const daysDiff = moment(arrivalDateTime.format('YYYY-MM-DD')).diff(
    moment(departDateTime.format('YYYY-MM-DD')),
    'days'
  )

  if (daysDiff) {
    const daysDiffDisplay = daysDiff > 0 ? `+${daysDiff}` : daysDiff

    if (getOnlyData) {
      return daysDiff
    }
    return <PlusDaySuper data-test='over-night-day-addition'>{String(daysDiffDisplay)}</PlusDaySuper>
  }
  return null
}

/** = Component */
export default class Slice extends React.Component {
  constructor(props, context) {
    super(props)
    const segmentList = this.props.slice.segments
    const hasMultipleAirlines = getAirlinesForThisSlice(segmentList, this.props.airlines).length > 1
    const isAirOdStopsToolTipOn = get(context, 'SETI.AIR_OD_STOPS_TOOL_TIP.isVariant', false)

    this.state = {
      isSliceCheckboxHovered: false,
      showHoverForComponent: false,
      sliceHasMultipleAirlines: hasMultipleAirlines,
      isAirOdStopsToolTipOn,
      showOvernightToolTip: false,
      arrivalHover: false,
      departureHover: false,
      airlineHover: false,
    }

    this.setCheckboxHover = this.setCheckboxHover.bind(this)
  }

  setCheckboxHover(isCheckboxHovered) {
    this.setState({ isSliceCheckboxHovered: isCheckboxHovered })
  }

  render() {
    const {
      airlines,
      sliceIndex,
      slice,
      tripType,
      openJaw,
      GDSCitySuggestion,
      hasNoHoverTooltipCookie,
      setNoTooltipOnHoverCookie,
      isCheckoutSlice,
      fareBrandClickHandler,
      fareBrandName,
      airportToCity,
      alternateType,
      itinerary,
      showFareBrandShelf,
      numberOfUpsells,
      showAirportToolTip,
    } = this.props
    const mobileSliceFilter = this.props.mobileSliceFilter
    const currentSliceIndex = this.props.sliceIndex
    const segments = slice.segments
    const sliceIdForFilter = currentSliceIndex + 1
    const airlinesForThisSlice = getAirlinesForThisSlice(segments, airlines)
    const departingAirportCode = slice?.departInfo?.airport?.code
    const arrivalAirportCode = slice?.arrivalInfo?.airport?.code
    const departingDateTime = slice?.departInfo?.time.dateTime
    const arrivalDateTime = slice?.arrivalInfo?.time.dateTime
    const departingTime = slice?.departInfo?.time?.displayTime
    const arrivalTime = slice?.arrivalInfo?.time?.displayTime
    const departingTitle = slice?.departInfo?.airport?.name
    const arrivalTitle = slice?.arrivalInfo?.airport?.name
    let stopsText

    switch (slice.stops) {
      case 0:
        stopsText = stopsLabel.nonStop
        break
      case 1:
        stopsText = stopsLabel.oneStop
        break
      default:
        stopsText = `${slice.stops} ${stopsLabel.stops}`
    }

    let highlightedAirportClass = {
      origin: '',
      destination: '',
    }

    if (openJaw || GDSCitySuggestion) {
      highlightedAirportClass = getAirportCode(currentSliceIndex, openJaw, GDSCitySuggestion)
    }

    // Screen Reader Only Text
    const srText = itinerary && buildReadableItinDescription(itinerary, tripType, true)[sliceIndex]
    const stopPropagation = (e) => e.stopPropagation()

    return (
      <StyledSlice
        data-test='pclnOne-slice'
        isSelected={slice.isSelected}
        showFareBrandShelf={showFareBrandShelf}
        numberOfUpsells={numberOfUpsells}
        mobileSliceFilter={mobileSliceFilter}
        isHovered={this.state.isSliceCheckboxHovered}
      >
        {this.context.excludeSliceFiltering || tripType === 'OW' || showFareBrandShelf ? null : (
          <StyledSliceSelection data-test='slice-selection-container' showFareBrandShelf={showFareBrandShelf}>
            <SliceCheckbox
              slice={slice}
              sliceIdForFilter={sliceIdForFilter}
              onHover={this.setCheckboxHover}
              isSelected={slice.isSelected}
              tripType={tripType}
              hasNoHoverTooltipCookie={hasNoHoverTooltipCookie}
              setNoTooltipOnHoverCookie={setNoTooltipOnHoverCookie}
              isHovered={this.state.isSliceCheckboxHovered}
            />
          </StyledSliceSelection>
        )}

        {!showFareBrandShelf && (
          <DayDateBox
            tripType={tripType}
            dateTime={departingDateTime}
            alternateType={alternateType}
            sliceIndex={sliceIndex}
            showFareBrandShelf={showFareBrandShelf}
          />
        )}

        <FormattedItineraryDetailFlex mobileSliceFilter={mobileSliceFilter}>
          {mobileSliceFilter && (
            <MobileSliceSelectionWrapper>
              <StyledSliceSelection
                data-test='slice-selection-container'
                showFareBrandShelf={showFareBrandShelf}
                mobileSliceFilter={mobileSliceFilter}
                aria-label={srText}
              >
                <SliceCheckbox
                  slice={slice}
                  mobileSliceFilter={mobileSliceFilter}
                  sliceIdForFilter={sliceIdForFilter}
                  onHover={this.setCheckboxHover}
                  isSelected={slice.isSelected}
                  tripType={tripType}
                  hasNoHoverTooltipCookie={hasNoHoverTooltipCookie}
                  setNoTooltipOnHoverCookie={setNoTooltipOnHoverCookie}
                  isHovered={this.state.isSliceCheckboxHovered}
                />
              </StyledSliceSelection>
            </MobileSliceSelectionWrapper>
          )}
          <ImageAndTitleText
            flexDirection='column'
            alignItems='center'
            data-testid='image-and-title-text'
            onMouseEnter={() => this.setState({ airlineHover: true })}
            onMouseLeave={() => this.setState({ airlineHover: false })}
            showFareBrandShelf={showFareBrandShelf}
          >
            {airlinesForThisSlice.length > 1 && (
              <StyledMultipleAirlinesWrapper
                justifyContent='center'
                alignItems='center'
                mb='2'
                ml='auto'
                mr='auto'
              >
                <MultipleAirlines />
              </StyledMultipleAirlinesWrapper>
            )}
            {airlinesForThisSlice.length === 1 && (
              <StyledAirlineImage
                airlineName={getAirlineName(airlinesForThisSlice)}
                airlineLogo={airlinesForThisSlice[0].image}
                className={this.props.itinIndex < this.context.alwaysRenderFirstN ? 'hero-image' : ''}
              />
            )}
            <Text regular>
              <AirlineTitle airlines={airlinesForThisSlice} />
            </Text>
            <Tooltip
              isHovered={this.state.airlineHover}
              tooltipContent={getAirlineName(airlinesForThisSlice)}
            />
          </ImageAndTitleText>

          <Flex>
            <TimeAndAirportItem
              data-testid='time-airport-departure'
              className='departure'
              isCheckoutSlice={isCheckoutSlice}
              showFareBrandShelf={showFareBrandShelf}
              onMouseEnter={() => {
                this.setState({ departureHover: true })
              }}
              onMouseLeave={() => {
                this.setState({ departureHover: false })
              }}
              // onClick prevents tooltip click from selecting listing
              onClick={stopPropagation}
            >
              <time dateTime={departingDateTime}>{departingTime}</time>
              <br />
              <abbr
                data-test='left-airport-code'
                title={!this.state.isAirOdStopsToolTipOn ? departingTitle : null}
                className={highlightedAirportClass.origin}
              >
                {departingAirportCode}
              </abbr>
              {this.state.departureHover && showAirportToolTip && (
                <StyledToolTipDivLeft>
                  <StyledToolTip bg='background.darkest' color='text.lightest' zIndex={1} top center>
                    {airportToCity[departingAirportCode]}
                  </StyledToolTip>
                </StyledToolTipDivLeft>
              )}
            </TimeAndAirportItem>

            <Stops
              number={slice.stops}
              layoverInfo={slice.layoverInfo}
              stopsText={showFareBrandShelf && slice.stops === 0 ? '' : stopsText}
              isAirOdStopsToolTipOn={this.state.isAirOdStopsToolTipOn}
              airportToCity={airportToCity}
              isShelfItinerary={showFareBrandShelf}
            />

            <TimeAndAirportItem
              data-testid='time-airport-arrival'
              className='arrival'
              isCheckoutSlice={isCheckoutSlice}
              showFareBrandShelf={showFareBrandShelf}
              onMouseEnter={() => {
                this.setState({ arrivalHover: true })
              }}
              onMouseLeave={() => {
                this.setState({ arrivalHover: false })
              }}
              // onClick prevents tooltip click from selecting listing
              onClick={stopPropagation}
            >
              <time dateTime={arrivalDateTime}>{arrivalTime}</time>
              {getOvernight(slice)}
              <br />
              <abbr
                data-test='right-airport-code'
                title={!this.state.isAirOdStopsToolTipOn ? arrivalTitle : null}
                className={highlightedAirportClass.destination}
              >
                {arrivalAirportCode}
              </abbr>
              {this.state.arrivalHover && showAirportToolTip && (
                <StyledToolTipDivRight>
                  <StyledToolTip bg='background.darkest' color='text.lightest' zIndex={1} top center>
                    {airportToCity[arrivalAirportCode]}
                  </StyledToolTip>
                </StyledToolTipDivRight>
              )}
            </TimeAndAirportItem>
          </Flex>

          {!showFareBrandShelf && (
            <DurationTimeBox mobileSliceFilter={mobileSliceFilter} data-test='duration-timebox'>
              {isCheckoutSlice ? (
                <StyledFareBrandButton
                  onClick={fareBrandClickHandler}
                  data-test='farebrand-name'
                  color={'primary.base'}
                >
                  {fareBrandName}
                </StyledFareBrandButton>
              ) : (
                <StyledStopsText>{stopsText}</StyledStopsText>
              )}
              <br />
              {slice.isOvernight ? (
                <OvernightBox
                  data-testid='overnight-box'
                  onMouseEnter={() => {
                    this.setState({ showOvernightToolTip: true })
                  }}
                  onMouseLeave={() => {
                    this.setState({ showOvernightToolTip: false })
                  }}
                  // onClick prevents tooltip click from selecting listing
                  onClick={stopPropagation}
                >
                  {this.state.showOvernightToolTip && showAirportToolTip && (
                    <StyledToolTip bg='background.darkest' color='text.lightest' zIndex={1} top center>
                      {'Overnight'}
                    </StyledToolTip>
                  )}
                  <StyledIcon data-name='overnight-icon' name='overnight' legacy={false} size={16} />
                </OvernightBox>
              ) : null}
              &nbsp;
              <time>{slice.duration}</time>
            </DurationTimeBox>
          )}
        </FormattedItineraryDetailFlex>
      </StyledSlice>
    )
  }
}

Slice.defaultProps = {
  alternateType: false,
  airportToCity: {},
  showFareBrandShelf: false,
  mobileSliceFilter: false,
}

Slice.propTypes = {
  openJaw: PropTypes.object,
  showAirportToolTip: PropTypes.bool,
  tripType: PropTypes.string,
  slice: PropTypes.any.isRequired,
  airlines: PropTypes.array.isRequired,
  sliceIndex: PropTypes.number.isRequired,
  hasNoHoverTooltipCookie: PropTypes.bool.isRequired,
  setNoTooltipOnHoverCookie: PropTypes.func.isRequired,
  itinerary: PropTypes.object.isRequired,
  isCheckoutSlice: PropTypes.bool,
  fareBrandClickHandler: PropTypes.func,
  fareBrandName: PropTypes.string,
  airportToCity: PropTypes.object,
  GDSCitySuggestion: PropTypes.object,
  alternateType: PropTypes.bool,
  showFareBrandShelf: PropTypes.bool,
  numberOfUpsells: PropTypes.number,
  mobileSliceFilter: PropTypes.bool,
  itinIndex: PropTypes.number,
}

Slice.contextTypes = {
  SETI: PropTypes.object,
  excludeSliceFiltering: PropTypes.bool,
  onSliceFilter: PropTypes.func,
  alwaysRenderFirstN: PropTypes.number,
}
